import { useEffect, useState } from "react";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { X, ChartBar, Globe, ArrowUUpLeft, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AnalyticsDialog({ link, onClose }) {
  const [data, setData] = useState(null);
  const [range, setRange] = useState(30);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/links/${link.link_id}/analytics?days=${range}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [link.link_id, range]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/links/${link.link_id}/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `clicks_${link.code}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const chart = (data?.timeseries || []).map((d) => ({
    date: d.date.slice(5),
    clicks: d.clicks,
  }));

  return (
    <div className="fixed inset-0 bg-black/85 flex items-center justify-center p-4 sm:p-6 z-50" onClick={onClose}>
      <div className="w-full max-w-3xl border border-white/15 bg-[#0a0a0a] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="analytics-dialog">
        <div className="sticky top-0 bg-[#0a0a0a] border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ChartBar size={18} weight="bold" />
            <h2 className="font-head text-lg font-bold">/r/{link.code}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportCsv} disabled={exporting} data-testid="export-csv-btn"
              className="flex items-center gap-1.5 mono text-xs uppercase tracking-wider border border-white/15 px-3 h-8 hover:border-white transition-colors disabled:opacity-50">
              <DownloadSimple size={14} /> {exporting ? "..." : "CSV"}
            </button>
            <button onClick={onClose} data-testid="close-analytics-btn" className="text-zinc-500 hover:text-white transition-colors"><X size={18} /></button>
          </div>
        </div>

        <div className="p-6">
          {/* range toggle + totals */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex gap-px bg-white/10 border border-white/10">
              {[7, 30, 90].map((r) => (
                <button key={r} onClick={() => setRange(r)} data-testid={`range-${r}`}
                  className={`px-4 h-9 mono text-xs uppercase tracking-wider transition-colors ${range === r ? "bg-white text-black" : "bg-[#0a0a0a] text-zinc-400 hover:text-white"}`}>
                  {r}d
                </button>
              ))}
            </div>
            <div className="flex gap-px bg-white/10 border border-white/10">
              <Metric label="Total clicks" value={data?.total_clicks ?? "—"} />
              <Metric label={`Last ${range}d`} value={data?.range_clicks ?? "—"} />
            </div>
          </div>

          {/* chart */}
          <p className="label-xs mb-3">// clicks over time</p>
          <div className="border border-white/10 p-4 h-[240px] mb-8">
            {loading ? (
              <div className="h-full flex items-center justify-center mono text-sm text-zinc-500">loading<span className="cursor-blink">_</span></div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#52525b", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} tickLine={false} interval="preserveStartEnd" />
                  <YAxis allowDecimals={false} tick={{ fill: "#52525b", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "rgba(255,255,255,0.05)" }}
                    contentStyle={{ background: "#000", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 }}
                    labelStyle={{ color: "#a1a1aa" }} itemStyle={{ color: "#fff" }} />
                  <Bar dataKey="clicks" fill="#ffffff" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* breakdowns */}
          <div className="grid sm:grid-cols-2 gap-6">
            <Breakdown icon={<ArrowUUpLeft size={14} />} title="top referrers" items={data?.top_referrers} testid="referrers-list" />
            <Breakdown icon={<Globe size={14} />} title="top countries" items={data?.top_countries} testid="countries-list" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="bg-[#0a0a0a] px-4 py-2">
      <div className="label-xs">{label}</div>
      <div className="font-head text-xl font-black tabular-nums">{value}</div>
    </div>
  );
}

function Breakdown({ icon, title, items, testid }) {
  const max = Math.max(1, ...(items || []).map((i) => i.count));
  return (
    <div data-testid={testid}>
      <div className="label-xs flex items-center gap-2 mb-3">{icon}{title}</div>
      {(!items || items.length === 0) && <p className="text-zinc-600 text-sm mono">no data yet</p>}
      <div className="space-y-2">
        {(items || []).map((i) => (
          <div key={i.name} className="relative border border-white/10 px-3 py-2 overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-white/[0.06]" style={{ width: `${(i.count / max) * 100}%` }} />
            <div className="relative flex items-center justify-between">
              <span className="mono text-xs text-zinc-300 truncate pr-2">{i.name}</span>
              <span className="mono text-xs tabular-nums text-white">{i.count}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
